/**
 * Created by neo on 02.12.20.
 *
 * Simplified model matching backend ActivitySchedule.kt
 * Backend pre-resolves all display properties (title, subtitle, media, showAsDone, etc.)
 * so no need for onBecomeObserved or complex update methods.
 */
import { makeAutoObservable } from 'mobx';
import { WeekDay, createWeekDay } from './WeekDay';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import dayjs, { Dayjs } from 'dayjs';
import { ScheduleMarkedDate, createScheduleMarkedDate } from './ScheduleMarkedDate';

export type SchedulerQueryRequest = {
  athleteId?: string;
  startDateTime?: string;
  endDateTime?: string;
  timezone?: string;
};

export type ActivitySchedule = {
  startDate: Dayjs;
  endDate: Dayjs;
  days: Record<string, WeekDay>;
  findWeekDay(date: Dayjs): WeekDay | undefined;
};

export function createActivitySchedule(json?: Partial<ActivitySchedule> & { days?: any[] }): ActivitySchedule {
  return makeAutoObservable({
    startDate: json?.startDate ? dayjs(json.startDate as any, 'YYYY-MM-DD') : dayjs(),
    endDate: json?.endDate ? dayjs(json.endDate as any, 'YYYY-MM-DD') : dayjs(),
    days: Array.isArray(json?.days)
      ? (json.days as any[]).reduce(
          (agg, d) => Object.assign(agg, { [d.date]: createWeekDay(d) }),
          {} as Record<string, WeekDay>,
        )
      : (json?.days ?? {}),

    findWeekDay(date: Dayjs): WeekDay | undefined {
      return this.days[date.format('YYYY-MM-DD')];
    },
  });
}

export const ActivityScheduleApi = {
  find(params?: SchedulerQueryRequest): Promise<ActivitySchedule> {
    return HttpBackend.get('/coach/schedule', params).then((res) => createActivitySchedule(res));
  },

  findDay(date: Dayjs): Promise<ActivitySchedule> {
    const dateStr = date.format('YYYY-MM-DD');
    return ActivityScheduleApi.find({ startDateTime: dateStr, endDateTime: dateStr });
  },

  findWeek(date: Dayjs): Promise<ActivitySchedule> {
    return ActivityScheduleApi.find({
      startDateTime: date.startOf('week').format('YYYY-MM-DD'),
      endDateTime: date.endOf('week').format('YYYY-MM-DD'),
    });
  },

  markedDates(params?: SchedulerQueryRequest): Promise<ScheduleMarkedDate[]> {
    return HttpBackend.get('/coach/schedule/markedDates', params).then((result) =>
      (result ?? []).map((res: any) => createScheduleMarkedDate(res)),
    );
  },

  markedDatesWeek(date: Dayjs): Promise<ScheduleMarkedDate[]> {
    return ActivityScheduleApi.markedDates({
      startDateTime: date.startOf('week').format('YYYY-MM-DD'),
      endDateTime: date.endOf('week').format('YYYY-MM-DD'),
    });
  },
};
